import "@fortawesome/fontawesome-pro/css/all.css";
import "./main.css";
import "./theme.d";

import { CssBaseline } from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  TypographyVariantsOptions,
} from "@mui/material/styles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { LicenseInfo } from "@mui/x-license";
import React from "react";

import { cardTheme } from "../ui-v2/Card/theme";
import { chipTheme } from "../ui-v2/Chip/theme";
import { Palette } from "./palette";

// This is a public license key and is not confidential.
LicenseInfo.setLicenseKey(
  "9adc1570828cbc9fc77684da3a290542Tz0xMTk3MTYsRT0xNzkwNTUzNTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI=",
);

// TODO: Colocate theme/types next to the component to avoid one giant file. For example move `typography` in `src/components/ui-v2/Typography`

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    h1: false;
    h2: false;
    h3: false;
    h4: false;
    h5: false;
    h6: false;
    subtitle1: false;
    subtitle2: false;
    body1: false;
    body2: false;
    title1: true;
    title2: true;
    title3: true;
    title4: true;
    large: true;
    regular: true;
    small: true;
    mini: true;
    micro: true;
  }
}

// Update Alert's severity prop options
declare module "@mui/material/Alert" {
  interface AlertPropsColorOverrides {
    secondary: true;
  }
}

const fontFamily = [
  "Outfit",
  "ui-sans-serif",
  "system-ui",
  "-apple-system",
  "BlinkMacSystemFont",
  "Segoe UI",
  "Roboto",
  "Helvetica Neue",
  "Arial",
  "Noto Sans",
  "sans-serif",
  "Apple Color Emoji",
  "Segoe UI Emoji",
  "Segoe UI Symbol",
  "Noto Color Emoji",
].join(",");

const typography: TypographyVariantsOptions = {
  fontFamily,
  h1: undefined,
  h2: undefined,
  h3: undefined,
  h4: undefined,
  h5: undefined,
  h6: undefined,
  subtitle1: undefined,
  subtitle2: undefined,
  body1: undefined,
  body2: undefined,
  fontSize: 14,
  title1: {
    fontSize: 36,
  },
  title2: {
    fontSize: 24,
  },
  title3: {
    fontSize: 22,
  },
  title4: {
    fontSize: 20,
  },
  large: {
    fontSize: 18,
  },
  regular: {
    fontSize: 14,
  },
  small: {
    fontSize: 13,
  },
  mini: {
    fontSize: 12,
  },
  micro: {
    fontSize: 11,
  },
};

export const theme = createTheme({
  typography,
  palette: {
    mode: "dark",
    primary: {
      dark: Palette.PRIMARY["dark/50"],
      main: Palette.PRIMARY["dark"],
      light: Palette.PRIMARY["light"],
      contrastText: Palette.SECONDARY[9],
    },
    secondary: {
      dark: Palette.SECONDARY[9],
      main: Palette.SECONDARY[8],
      light: Palette.SECONDARY[7],
      contrastText: Palette.SECONDARY[2],
      9: Palette.SECONDARY[9],
      "9/75": Palette.SECONDARY["9/75"],
      "9/50": Palette.SECONDARY["9/50"],
      "9/25": Palette.SECONDARY["9/25"],
      8: Palette.SECONDARY[8],
      "8/75": Palette.SECONDARY["8/75"],
      "8/50": Palette.SECONDARY["8/50"],
      "8/25": Palette.SECONDARY["8/25"],
      7: Palette.SECONDARY[7],
      "7/75": Palette.SECONDARY["7/75"],
      "7/50": Palette.SECONDARY["7/50"],
      "7/25": Palette.SECONDARY["7/25"],
      6: Palette.SECONDARY[6],
      "6/75": Palette.SECONDARY["6/75"],
      "6/50": Palette.SECONDARY["6/50"],
      "6/25": Palette.SECONDARY["6/25"],
      5: Palette.SECONDARY[5],
      "5/75": Palette.SECONDARY["5/75"],
      "5/50": Palette.SECONDARY["5/50"],
      "5/25": Palette.SECONDARY["5/25"],
      4: Palette.SECONDARY[4],
      "4/75": Palette.SECONDARY["4/75"],
      "4/50": Palette.SECONDARY["4/50"],
      "4/25": Palette.SECONDARY["4/25"],
      3: Palette.SECONDARY[3],
      "3/75": Palette.SECONDARY["3/75"],
      "3/50": Palette.SECONDARY["3/50"],
      "3/25": Palette.SECONDARY["3/25"],
      2: Palette.SECONDARY[2],
      "2/75": Palette.SECONDARY["2/75"],
      "2/50": Palette.SECONDARY["2/50"],
      "2/25": Palette.SECONDARY["2/25"],
      1: Palette.SECONDARY[1],
      "1/75": Palette.SECONDARY["1/75"],
      "1/50": Palette.SECONDARY["1/50"],
      "1/25": Palette.SECONDARY["1/25"],
    },
    success: {
      dark: Palette.SUCCESS["dark"],
      main: Palette.SUCCESS["main"],
      light: Palette.SUCCESS["light"],
      contrastText: Palette.SECONDARY[9],
    },
    warning: {
      dark: Palette.WARNING["dark"],
      main: Palette.WARNING["main"],
      light: Palette.WARNING["light"],
      contrastText: Palette.SECONDARY[9],
    },
    error: {
      dark: Palette.ERROR["dark"],
      main: Palette.ERROR["main"],
      light: Palette.ERROR["light"],
      contrastText: Palette.SECONDARY[9],
    },
    model: {
      dark: Palette.MODEL["dark"],
      main: Palette.MODEL["main"],
      light: Palette.MODEL["light"],
      contrastText: Palette.SECONDARY[9],
    },
    node: {
      generator: Palette.NODE_GENERATOR,
      prompt: Palette.NODE_PROMPT,
      style: Palette.NODE_STYLE,
      insert: Palette.NODE_INSERT,
      reference: Palette.NODE_REFERENCE,
      utility: Palette.NODE_UTILITY,
      refine: Palette.NODE_REFINE,
      disabled: Palette.NODE_DISABLED,
    },
  },
  spacing: 4,
  shape: {
    borderRadius: 2,
  },
  components: {
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: "0.875em",
          verticalAlign: "-0.0714em",
          width: "1.25em",
          overflow: "visible",
        },
        fontSizeSmall: {
          fontSize: "0.75em",
          width: "1em",
        },
        fontSizeMedium: {
          fontSize: "1em",
          width: "1.25em",
        },
        fontSizeLarge: {
          fontSize: "1.25em",
          width: "1.5625em",
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily,
          textAlign: "left",
          backgroundColor: Palette.SECONDARY[9],
        },
        a: {
          color: "inherit",
          textDecoration: "inherit",
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          "& .MuiAutocomplete-listbox": {
            "& .MuiAutocomplete-option": {
              fontSize: 13,
            },
          },
          "& .MuiListSubheader-root": {
            lineHeight: "40px",
            fontSize: 13,
            color: Palette.SECONDARY[3],
          },
          "& .MuiAutocomplete-noOptions": {
            fontSize: 13,
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: "inherit",
          fontSize: 14,
        },
      },
      defaultProps: {
        color: "inherit",
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: Palette.SECONDARY[6],
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          fontSize: typography.regular.fontSize,
          "& span": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: Palette.SECONDARY[3],
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: Palette.SECONDARY[7],
          backgroundImage: "none",
          borderRadius: "8px",
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: Palette.SECONDARY[7],
          border: "1px solid",
          borderColor: Palette.SECONDARY[6],
        },
      },
      defaultProps: {
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
        transformOrigin: {
          vertical: "top",
          horizontal: "left",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        root: {
          fontSize: typography.small.fontSize,
          lineHeight: typography.small.lineHeight,
          "& .MuiDivider-root": {
            borderColor: Palette.SECONDARY[5],
            margin: "8px 0",
          },
        },
        paper: {
          minWidth: 210,
          backgroundColor: Palette.SECONDARY[6],
          border: `1px solid ${Palette.SECONDARY[5]}`,
          boxShadow: "0px 7px 32px 0px rgba(0, 0, 0, 0.35)",
          borderRadius: "8px",
        },
      },
    },
    MuiMenuList: {
      styleOverrides: {
        root: {
          fontSize: typography.small.fontSize,
          lineHeight: typography.small.lineHeight,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: Palette.SECONDARY[5],
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          color: Palette.SECONDARY[2],
          paddingTop: 24,
          paddingBottom: 0,
          fontSize: "13px",
          position: "relative",
          zIndex: 1,

          "& .MuiSlider-thumb": {
            color: Palette.SECONDARY[2],
            height: "20px",
            width: "20px",
          },

          "& .MuiSlider-thumb:hover": {
            color: Palette.SECONDARY[1],
            boxShadow: "none",
          },

          "& .MuiSlider-track": {
            backgroundColor: Palette.SECONDARY[3],
            border: "none",
            height: "16px",
          },

          "& .MuiSlider-rail": {
            opacity: 1,
            height: "16px",
            backgroundColor: Palette.SECONDARY[4],
            boxShadow:
              "0px 0.5px 0px 0px rgba(226, 186, 248, 0.08) inset, 0px 0px 0.5px 0px rgba(226, 186, 248, 0.30) inset",
          },

          "& .MuiSlider-mark": {
            display: "hidden !important",
            opacity: 0.25,
            height: "12px",
            width: "1px",
          },

          "& .MuiSlider-thumb:before": {
            boxShadow: "none !important",
          },

          '& .MuiSlider-mark[data-index="0"], .MuiSlider-mark[style*="left: 0%"], .MuiSlider-mark[style*="left: 100%"]':
            {
              visibility: "hidden",
            },
        },
        sizeSmall: {
          paddingTop: 16,
          "&& .MuiSlider-thumb": {
            height: "16px",
            width: "16px",
          },
          "&& .MuiSlider-track": {
            height: "12px",
          },
          "&& .MuiSlider-rail": {
            height: "12px",
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: "4px 4px",
          margin: "2px 8px",
          borderRadius: 4,
          border: "none",
          minHeight: "32px",
          "& .MuiListItemText-root": {
            flex: 1,
            display: "flex",
            justifyContent: "space-between",
            gap: "12px",
          },
          "& .MuiListItemIcon-root": {
            minHeight: "24px",
            minWidth: "24px",
            height: "24px",
            width: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: "4px",
          },
          "& .MuiListItemText-primary, & .MuiListItemText-secondary": {
            fontSize: typography.small.fontSize,
          },
          "& .MuiListItemText-secondary": {
            color: Palette.SECONDARY[3],
          },
          "&.Mui-focusVisible": {
            backgroundColor: Palette.SECONDARY[6],
            borderColor: Palette.SECONDARY[5],
          },
          "&.Mui-selected, &.Mui-selected:hover": {
            backgroundColor: Palette.SECONDARY[6],
            boxShadow: `inset 0 0 0 0.5px ${Palette.SECONDARY[4]}`,
            color: Palette.SECONDARY[1],
          },
          ":hover": {
            backgroundColor: Palette.SECONDARY[5],
            boxShadow: `inset 0 0 0 0.5px ${Palette.SECONDARY[4]}`,
          },
          "& .MuiTouchRipple-root": {
            display: "none",
          },
          "&+.MuiDivider-root": {
            margin: "8px 0",
          },
          variants: [
            {
              props: {
                variant: "primary",
              },
              style: {
                ".MuiListItemText-root": {
                  color: Palette.SECONDARY[1],
                  fontWeight: 500,
                },
                ".MuiListItemIcon-root": {
                  color: Palette.SECONDARY[1],
                },
              },
            },
            {
              props: {
                variant: "secondary",
              },
              style: {
                ".MuiListItemText-root": {
                  color: Palette.SECONDARY[1],
                  fontWeight: 400,
                },
                ".MuiListItemIcon-root": {
                  color: Palette.SECONDARY[3],
                },
              },
            },
            {
              props: {
                variant: "tertiary",
              },
              style: {
                ".MuiListItemText-root": {
                  color: Palette.SECONDARY[3],
                  fontWeight: 400,
                },
                ".MuiListItemIcon-root": {
                  color: Palette.SECONDARY[3],
                },
              },
            },
          ],
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 24,
          color: Palette.SECONDARY[3],
          "& svg": {
            fontSize: "inherit",
          },
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          fontSize: 14,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: Palette.WHITE,
          borderRadius: 4,
          border: `1px solid ${Palette.TRANSPARENT}`,

          "&:disabled": {
            opacity: 0.5,
          },
        },
        sizeLarge: {
          padding: 8,
          fontSize: 13,
          minWidth: 32,
          minHeight: 32,
          maxHeight: 32,
        },
        sizeMedium: {
          padding: 6,
          fontSize: 13,
          minWidth: 28,
          minHeight: 28,
          maxHeight: 28,
        },
        sizeSmall: {
          padding: 6,
          fontSize: 12,
          minWidth: 24,
          minHeight: 24,
          maxHeight: 24,
        },
      },
    },
    MuiLoadingButton: {
      defaultProps: {
        variant: "contained",
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            opacity: 0.3,
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          padding: "4px 8px",
          borderRadius: "4px",
          "&.Mui-selected": {
            "&.MuiToggleButton-primary": {
              backgroundColor: Palette.PRIMARY.light,
              color: Palette.SECONDARY[9],
            },
            "&.MuiToggleButton-secondary": {
              color: Palette.PRIMARY.main,
            },
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          "& .MuiSwitch-track": {
            backgroundColor: "transparent",
            border: "1px solid",
            borderColor: Palette.SECONDARY[3],
            borderRadius: "20px",
            opacity: 1,
          },

          "& .MuiSwitch-thumb": {
            boxShadow: "none",
          },

          ".MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track": {
            backgroundColor: Palette.SECONDARY[2],
            borderColor: Palette.SECONDARY[2],
            opacity: 1,
          },
        },
        sizeMedium: {
          padding: "8px",
          "& .MuiSwitch-thumb": {
            background: `radial-gradient(${Palette.SECONDARY[3]} 8px, transparent 8px)`,
          },
          ".Mui-checked .MuiSwitch-thumb": {
            background: `radial-gradient(${Palette.SECONDARY[7]} 9px, transparent 9px)`,
          },
        },
        sizeSmall: {
          padding: "2px",
          "& .MuiSwitch-thumb": {
            background: `radial-gradient(${Palette.SECONDARY[3]} 7px, transparent 7px)`,
          },
          ".Mui-checked .MuiSwitch-thumb": {
            background: `radial-gradient(${Palette.SECONDARY[7]} 8px, transparent 8px)`,
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          "&:disabled, &.Mui-disabled": {
            opacity: 0.5,
          },
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: {
            rounded: true,
          },
          style: {
            borderRadius: "9999px",
            paddingLeft: "16px",
            paddingRight: "16px",
          },
        },
      ],

      styleOverrides: {
        // All Buttons
        root: {
          textTransform: "none",
          boxSizing: "border-box",
          border: "none",
          whiteSpace: "nowrap",
          borderRadius: 4,
          fontWeight: 500,
          height: "fit-content",
          width: "fit-content",
          minWidth: "unset",

          "&.MuiButton-fullWidth": {
            width: "100%",
            minWidth: "100%",

            "& .MuiButton-endIcon": {
              marginLeft: "auto",
            },
          },

          "& .MuiButton-loadingIndicatorStart": {
            left: 6,
          },

          "& .MuiButton-startIcon": {
            fontSize: 13,
          },

          "& .MuiButton-endIcon": {
            fontSize: 13,
            marginBottom: -1,
          },

          "&.MuiButton-sizeLarge": {
            "&.MuiButton-startIcon": {
              fontSize: 13,
            },

            "&.MuiButton-endIcon": {
              fontSize: 13,
            },
          },

          "&.MuiButton-sizeMedium": {
            "&.MuiButton-startIcon": {
              fontSize: 13,
            },

            "&.MuiButton-endIcon": {
              fontSize: 13,
            },
          },

          "&.MuiButton-sizeSmall": {
            "&.MuiButton-startIcon": {
              fontSize: 12,
            },

            "&.MuiButton-endIcon": {
              fontSize: 12,
            },
          },
        },
        // Contained
        containedPrimary: {
          backgroundColor: Palette.PRIMARY.dark,
          boxShadow: `inset 0 0 0 0.5px ${Palette.PRIMARY["light/50"]}`,
          color: Palette.WHITE,

          "&.MuiButton-endIcon": {
            color: Palette.PRIMARY.light,
          },

          "&:hover": {
            backgroundColor: Palette.PRIMARY.light,
            boxShadow: `inset 0 0 0 0.5px ${Palette.PRIMARY.light}`,
            color: Palette.SECONDARY[9],
          },

          "&:disabled": {
            backgroundColor: Palette.PRIMARY.dark,
            color: Palette.PRIMARY.light,
            opacity: 0.5,
          },
        },
        containedSecondary: {
          border: "none",
          backgroundColor: Palette.SECONDARY[5],
          boxShadow: `inset 0 0 0 0.5px ${Palette.SECONDARY[4]}`,

          "&.MuiButton-endIcon": {
            color: Palette.SECONDARY[3],
          },

          "&:hover": {
            border: "none",
            backgroundColor: Palette.SECONDARY[5],
            boxShadow: `inset 0 0 0 0.5px ${Palette.SECONDARY["3/50"]}`,
          },

          "&:disabled": {
            backgroundColor: Palette.SECONDARY["5/50"],
            color: Palette.SECONDARY[3],
            opacity: 0.5,
          },

          "& .MuiButton-startIcon": {
            color: Palette.SECONDARY[3],
          },
        },
        containedSuccess: {
          backgroundColor: Palette.SUCCESS["main"],
          boxShadow: `inset 0 0 0 0.5px ${Palette.SUCCESS["light"]}`,
          color: Palette.SECONDARY[9],

          "&.MuiButton-endIcon": {
            color: Palette.SUCCESS["main"],
          },

          "&:hover": {
            backgroundColor: Palette.SUCCESS["light"],
          },

          "&:disabled": {
            backgroundColor: Palette.SUCCESS["dark"],
            color: Palette.SECONDARY["9/75"],
            opacity: 0.5,
          },
        },
        containedWarning: {
          backgroundColor: Palette.WARNING["main"],
          boxShadow: `inset 0 0 0 0.5px ${Palette.WARNING["light"]}`,
          color: Palette.SECONDARY[8],

          "&.MuiButton-endIcon": {
            color: Palette.SECONDARY[4],
          },

          "&:hover": {
            backgroundColor: Palette.WARNING["light"],
          },

          "&:disabled": {
            backgroundColor: Palette.WARNING["dark"],
            color: Palette.SECONDARY["8/75"],
            opacity: 0.5,
          },
        },
        containedError: {
          backgroundColor: Palette.ERROR["main"],
          boxShadow: `inset 0 0 0 0.5px ${Palette.ERROR["light"]}`,
          color: Palette.SECONDARY[8],

          "&.MuiButton-endIcon": {
            color: Palette.ERROR["main"],
          },

          "&:hover": {
            backgroundColor: Palette.ERROR["light"],
            color: Palette.SECONDARY[9],
          },

          "&:disabled": {
            backgroundColor: Palette.ERROR["dark"],
            color: Palette.SECONDARY["8/75"],
            opacity: 0.5,
          },
        },
        // Outlined
        outlined: {
          border: "none",
          backgroundColor: "rgba(0,0,0,0.01)",

          "&:hover": {
            border: "none",
          },

          "&:disabled": {
            border: "none",
          },
        },
        outlinedPrimary: {
          boxShadow: `inset 0 0 0 0.5px ${Palette.PRIMARY.main}`,
          color: Palette.PRIMARY.light,

          "&.MuiButton-endIcon": {
            color: Palette.PRIMARY.light,
          },

          "&:hover": {
            boxShadow: `inset 0 0 0 0.5px ${Palette.PRIMARY.light}`,
            color: Palette.PRIMARY.light,
          },

          "&:disabled": {
            boxShadow: `inset 0 0 0 0.5px ${Palette.PRIMARY.main}`,
            color: Palette.PRIMARY.light,
            opacity: 0.5,
          },
        },
        outlinedSecondary: {
          boxShadow: `inset 0 0 0 0.5px ${Palette.SECONDARY["3/50"]}`,
          color: Palette.SECONDARY[2],

          "&.MuiButton-endIcon": {
            color: Palette.SECONDARY[3],
          },

          "&:hover": {
            boxShadow: `inset 0 0 0 0.5px ${Palette.SECONDARY[3]}`,
            color: Palette.SECONDARY[1],
          },

          "&:disabled": {
            boxShadow: `inset 0 0 0 0.5px ${Palette.SECONDARY[5]}`,
            color: Palette.SECONDARY[3],
            opacity: 0.5,
          },
        },
        outlinedSuccess: {
          boxShadow: `inset 0 0 0 0.5px ${Palette.SUCCESS.dark}`,
          color: Palette.SUCCESS["main"],

          "&.MuiButton-endIcon": {
            color: Palette.SUCCESS["main"],
          },

          "&:hover": {
            boxShadow: `inset 0 0 0 0.5px ${Palette.SUCCESS.main}`,
            color: Palette.SUCCESS["light"],
          },

          "&:disabled": {
            boxShadow: `inset 0 0 0 0.5px ${Palette.SUCCESS.dark}`,
            color: Palette.SUCCESS["main"],
            opacity: 0.5,
          },
        },
        outlinedWarning: {
          boxShadow: `inset 0 0 0 0.5px ${Palette.WARNING.dark}`,
          color: Palette.WARNING["main"],

          "&.MuiButton-endIcon": {
            color: Palette.WARNING["main"],
          },

          "&:hover": {
            boxShadow: `inset 0 0 0 0.5px ${Palette.WARNING.main}`,
            color: Palette.WARNING["light"],
          },

          "&:disabled": {
            boxShadow: `inset 0 0 0 0.5px ${Palette.WARNING.dark}`,
            color: Palette.WARNING["main"],
            opacity: 0.5,
          },
        },
        outlinedError: {
          boxShadow: `inset 0 0 0 0.5px ${Palette.ERROR.dark}`,
          color: Palette.ERROR["main"],

          "&.MuiButton-endIcon": {
            color: Palette.ERROR["main"],
          },

          "&:hover": {
            boxShadow: `inset 0 0 0 0.5px ${Palette.ERROR.main}`,
            color: Palette.ERROR["light"],
          },

          "&:disabled": {
            boxShadow: `inset 0 0 0 0.5px ${Palette.ERROR.dark}`,
            color: Palette.ERROR["main"],
            opacity: 0.5,
          },
        },
        // Text
        text: {
          textAlign: "left",
          border: "none",
        },
        textPrimary: {
          color: Palette.PRIMARY.light,

          "&.MuiButton-endIcon": {
            color: Palette.PRIMARY.light,
          },

          "&:hover": {
            color: Palette.PRIMARY.light,
          },

          "&:disabled": {
            color: Palette.PRIMARY.light,
            opacity: 0.5,
          },
        },
        textSecondary: {
          color: Palette.SECONDARY[2],

          "&.MuiButton-startIcon": {
            color: Palette.SECONDARY[3],
          },

          "&.MuiButton-endIcon": {
            color: Palette.SECONDARY[4],
          },

          "&:hover": {
            backgroundColor: Palette.SECONDARY["6/75"],
            color: Palette.SECONDARY[1],
          },

          "&:disabled": {
            color: Palette.SECONDARY[3],
            opacity: 0.5,
          },
        },
        textSuccess: {
          color: Palette.SUCCESS["main"],

          "&.MuiButton-endIcon": {
            color: Palette.SUCCESS["main"],
          },

          "&:hover": {
            color: Palette.SUCCESS["main"],
          },

          "&:disabled": {
            color: Palette.SUCCESS["main"],
            opacity: 0.5,
          },
        },
        textWarning: {
          color: Palette.WARNING["main"],

          "&.MuiButton-endIcon": {
            color: Palette.WARNING["main"],
          },

          "&:hover": {
            color: Palette.WARNING["main"],
          },

          "&:disabled": {
            color: Palette.WARNING["main"],
            opacity: 0.5,
          },
        },
        textError: {
          color: Palette.ERROR["main"],

          "&.MuiButton-endIcon": {
            color: Palette.ERROR["main"],
          },

          "&:hover": {
            color: Palette.ERROR["main"],
          },

          "&:disabled": {
            color: Palette.ERROR["main"],
            opacity: 0.5,
          },
        },
        // Icon
        startIcon: {
          "& >*:nth-of-type(1)": {
            fontSize: "inherit",
          },
        },
        endIcon: {
          "& >*:nth-of-type(1)": {
            fontSize: "inherit",
          },
        },
        // Sizes
        sizeLarge: {
          fontSize: 14,
          minHeight: 40,
          maxHeight: 40,
          height: "100%",

          "&.MuiButton-startIcon": {
            fontSize: 14,
            paddingRight: 5,
          },

          "&.MuiButton-endIcon": {
            fontSize: 14,
          },
        },
        sizeMedium: {
          fontSize: 14,
          minHeight: 28,
          maxHeight: 28,
          height: "100%",

          "&.MuiButton-startIcon": {
            fontSize: 14,
          },

          "&.MuiButton-endIcon": {
            fontSize: 14,
          },

          "& .MuiCircularProgress-root ": {
            height: 14,
            width: 14,
          },
        },
        sizeSmall: {
          fontSize: 12,
          minHeight: 24,
          maxHeight: 24,
          height: "100%",

          "&.MuiButton-startIcon": {
            fontSize: 12,
          },

          "&.MuiButton-endIcon": {
            fontSize: 12,
          },
        },
        iconSizeSmall: {
          fontSize: 12,
        },
        iconSizeMedium: {
          fontSize: 16,
        },
        iconSizeLarge: {
          fontSize: 18,
        },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        grouped: {
          minWidth: 20,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&.Mui-focused": {
            color: Palette.PRIMARY.light,
          },
          "&.Mui-disabled": {
            opacity: 0.3,
          },
        },
        animated: {
          color: Palette.SECONDARY[3],
          fontSize: typography.small.fontSize,
        },
        shrink: {
          transform: "scale(0.857142857)",
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variant: "regular",
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          ":before": {
            borderBottom: "0 !important",
          },
          ":after": {
            borderBottom: "0 !important",
          },
        },
        standard: {
          ":focus": {
            backgroundColor: "transparent",
          },
        },
      },
    },
    ...cardTheme,
    ...chipTheme,
    MuiTable: {
      styleOverrides: {
        root: {
          fontSize: typography.small.fontSize,
          backgroundColor: Palette.SECONDARY[8], // Make sure the bgcolor is always the same as the sticky header
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          color: Palette.SECONDARY[2],
          borderBottom: "none",
        },
        head: {
          color: Palette.SECONDARY[3],
          borderBottom: "1px solid",
          borderColor: Palette.SECONDARY[5],
          paddingTop: 4,
          paddingBottom: 3,
        },
        stickyHeader: {
          backgroundColor: Palette.SECONDARY[8], // Prevent the cells below the header from being visible when scrolling, because default is transparent
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          borderRadius: "4px",
          textTransform: "none",
          minHeight: "auto",
          minWidth: "auto",
          padding: "4px 8px",
          fontSize: "14px",
          fontWeight: "normal",
          gap: "4px",
          "&.Mui-selected": {
            color: Palette.SECONDARY[1],
            border: `1px solid ${Palette.SECONDARY[7]}`,
            backgroundColor: Palette.SECONDARY[8],
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: "auto",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&&": {
            minHeight: "28px",
            fontSize: "14px",
          },
          "&& fieldset": {
            borderColor: "transparent",
            borderRadius: "4px",
          },
          "&&:hover fieldset": {
            borderColor: Palette.SECONDARY[5],
          },
          "&&.Mui-disabled fieldset": {
            borderColor: Palette.SECONDARY[8],
          },
          "&&.MuiInputBase-root.Mui-disabled": {
            color: Palette.SECONDARY[3],

            input: {
              color: Palette.SECONDARY[3],
              WebkitTextFillColor: Palette.SECONDARY[3],
            },
          },
        },
        input: {
          padding: "0 8px",
        },
        adornedEnd: {
          marginLeft: "0!important",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-root.Mui-disabled": {
            opacity: 0.3,
          },
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          paddingLeft: 0,
          paddingRight: 0,
          paddingTop: 0,
          paddingBottom: 0,
          fontSize: "14px",
          lineHeight: "1.25rem",
        },
      },
    },
    MuiStep: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          padding: "1rem 0 0",
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          color: Palette.SECONDARY[3],
          "&.Mui-active": {
            color: Palette.SECONDARY[1],
          },
        },
        iconContainer: {
          "&.Mui-completed": {
            color: Palette.MODEL["main"],
            fontWeight: 600,
          },
          "&.Mui-active": {
            color: Palette.MODEL["main"],
            fontWeight: 600,
          },
          "&.Mui-disabled": {
            color: Palette.SECONDARY[3],
            fontWeight: 600,
          },
        },
      },
    },
    MuiStepButton: {
      styleOverrides: {
        root: {
          lineHeight: "inherit",
          fontSize: "inherit",
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: "4px",
          padding: 0,
        },
        message: {
          display: "flex",
          alignItems: "center",
          fontWeight: 600,
          fontSize: 13,
        },
        icon: {
          marginRight: "8px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
        standard: {
          border: "none",
        },
        standardInfo: {
          backgroundColor: Palette.PRIMARY.light,
          borderColor: Palette.PRIMARY.light,
          color: Palette.SECONDARY[9],

          "& .MuiAlert-action": {
            "& .MuiButtonBase-root": {
              color: Palette.SECONDARY[9],
              backgroundColor: Palette.SECONDARY["9/25"],
              "&:hover": {
                backgroundColor: Palette.SECONDARY["9/50"],
                boxShadow: "none",
              },
            },

            "& .MuiIconButton-root": {
              color: Palette.SECONDARY[9],
              backgroundColor: Palette.SECONDARY["9/25"],
              "&:hover": {
                backgroundColor: Palette.SECONDARY["9/50"],
              },
            },
          },

          "& .MuiAlert-icon": {
            color: Palette.SECONDARY[9],
          },
        },
        standardSuccess: {
          backgroundColor: Palette.SUCCESS["main"],
          borderColor: Palette.SUCCESS["main"],
          color: Palette.SECONDARY[9],

          "& .MuiAlert-action": {
            "& .MuiButtonBase-root": {
              color: Palette.SECONDARY[9],
              backgroundColor: Palette.SECONDARY["9/25"],
              "&:hover": {
                backgroundColor: Palette.SECONDARY["9/50"],
                boxShadow: "none",
              },
            },

            "& .MuiIconButton-root": {
              color: Palette.SECONDARY[9],
              backgroundColor: Palette.SECONDARY["9/25"],
              "&:hover": {
                backgroundColor: Palette.SECONDARY["9/50"],
              },
            },
          },

          "& .MuiAlert-icon": {
            color: Palette.SECONDARY[9],
          },
        },
        standardWarning: {
          backgroundColor: Palette.WARNING["main"],
          borderColor: Palette.WARNING["main"],
          color: Palette.SECONDARY[9],

          "& .MuiAlert-action": {
            "& .MuiButtonBase-root": {
              color: Palette.SECONDARY[9],
              backgroundColor: Palette.SECONDARY["9/25"],
              "&:hover": {
                backgroundColor: Palette.SECONDARY["9/50"],
                boxShadow: "none",
              },
            },

            "& .MuiIconButton-root": {
              color: Palette.SECONDARY[9],
              backgroundColor: Palette.SECONDARY["9/25"],
              "&:hover": {
                backgroundColor: Palette.SECONDARY["9/50"],
              },
            },
          },

          "& .MuiAlert-icon": {
            color: Palette.SECONDARY[9],
          },
        },
        standardError: {
          backgroundColor: Palette.ERROR["main"],
          borderColor: Palette.ERROR["main"],
          color: Palette.SECONDARY[9],

          "& .MuiAlert-action": {
            "& .MuiButtonBase-root": {
              color: Palette.SECONDARY[9],
              backgroundColor: Palette.SECONDARY["9/25"],
              "&:hover": {
                backgroundColor: Palette.SECONDARY["9/50"],
                boxShadow: "none",
              },
            },

            "& .MuiIconButton-root": {
              color: Palette.SECONDARY[9],
              backgroundColor: Palette.SECONDARY["9/25"],
              "&:hover": {
                backgroundColor: Palette.SECONDARY["9/50"],
              },
            },
          },

          "& .MuiAlert-icon": {
            color: Palette.SECONDARY[9],
          },
        },
        // @ts-ignore - this field is valid, but typescript is unhappy about it
        standardSecondary: {
          backgroundColor: Palette.SECONDARY[1],
          borderColor: Palette.SECONDARY[1],
          color: Palette.SECONDARY[9],

          "& .MuiAlert-action": {
            "& .MuiButtonBase-root": {
              color: Palette.SECONDARY[9],
              backgroundColor: Palette.SECONDARY["9/25"],
              "&:hover": {
                backgroundColor: Palette.SECONDARY["9/50"],
                boxShadow: "none",
              },
            },

            "& .MuiIconButton-root": {
              color: Palette.SECONDARY[9],
              backgroundColor: Palette.SECONDARY["9/25"],
              "&:hover": {
                backgroundColor: Palette.SECONDARY["9/50"],
              },
            },
          },

          "& .MuiAlert-icon": {
            color: Palette.SECONDARY[9],
          },
        },
        outlinedSecondary: {
          border: `1px solid ${Palette.SECONDARY[5]}`,
          backgroundColor: Palette.SECONDARY[6],
          color: Palette.SECONDARY[1],

          "& .MuiAlert-action": {
            "& .MuiButtonBase-root": {
              color: Palette.SECONDARY[1],
              backgroundColor: Palette.SECONDARY["9/25"],
              "&:hover": {
                backgroundColor: Palette.SECONDARY["9/50"],
                boxShadow: "none",
              },
            },

            "& .MuiIconButton-root": {
              color: Palette.SECONDARY[1],
              backgroundColor: Palette.SECONDARY["9/25"],
              "&:hover": {
                backgroundColor: Palette.SECONDARY["9/50"],
              },
            },
          },

          "& .MuiAlert-icon": {
            color: Palette.SECONDARY[1],
          },
        },
        action: {
          padding: "0 4px",
          marginRight: 0,
          alignItems: "center",
          justifyContent: "center",

          "& .MuiButtonBase-root": {
            height: "100%",
            maxHeight: "100%",
            boxShadow: "none",
          },
          "& .MuiIconButton-root": {
            height: "100%",
            maxHeight: "100%",
            aspectRatio: "1/1",
            padding: "2px",
            display: "flex",
            alignItems: "center",
          },
        },
      },
    },
    MuiAvatarGroup: {
      styleOverrides: {
        root: {
          "& .MuiAvatar-root": {
            border: `0.5px solid ${Palette.SECONDARY[6]}`,
          },
        },
        avatar: {
          backgroundColor: Palette.SECONDARY[4],
          color: Palette.SECONDARY[1],
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 16,
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        enterDelay: 5,
        enterNextDelay: 0,
      },
      styleOverrides: {
        tooltip: {
          borderRadius: "4px",
          backgroundColor: Palette.SECONDARY[1],
          color: Palette.SECONDARY[7],
        },
        arrow: {
          color: Palette.SECONDARY[1],
        },
      },
    },
    MuiBreadcrumbs: {
      defaultProps: {
        separator: "›",
      },
      styleOverrides: {
        root: {
          fontSize: 13,

          "& .MuiBreadcrumbs-separator": {
            color: Palette.SECONDARY[3],
            marginTop: "-3px",
            fontSize: "18px",
          },

          "&& .MuiBreadcrumbs-li": {
            color: Palette.SECONDARY[3],

            "&:hover": {
              color: Palette.SECONDARY[1],
            },

            "&:last-child": {
              color: Palette.SECONDARY[1],
            },
          },
        },
      },
    },
    MuiDateRangeCalendar: {
      styleOverrides: {
        root: {
          backgroundColor: Palette.SECONDARY[6],

          "& .MuiDateRangePickerDay-root": {
            "&:focus": {
              color: Palette.SECONDARY[1],
            },

            "& .Mui-selected": {
              backgroundColor: Palette.PRIMARY.light,
              color: Palette.BLACK,

              "&:hover": {
                color: Palette.SECONDARY[1],
              },
            },

            "& .MuiPickersDay-today": {
              borderColor: Palette.SECONDARY[4],
            },
          },

          "& .MuiDateRangePickerDay-rangeIntervalDayHighlight": {
            backgroundColor: Palette.SECONDARY[5],
          },
        },
      },
    },
  },
});

export const LayerThemeProvider = ({ children }: React.PropsWithChildren) => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        {children}
      </LocalizationProvider>
    </ThemeProvider>
  );
};
